import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import {
  type InviteAuthHint,
  inviteTruthChecklist,
  joinReadinessForInvite,
} from "../data/invite-join-readiness";
import {
  sendMemberAuthInviteEmail,
  successNoteForAuthInvite,
} from "../data/member-auth-invite";
import {
  fetchMemberInviteHistory,
  MEMBER_INVITE_HISTORY_LIMIT,
  type MemberInviteHistoryPage,
  type MemberInviteHistoryRow,
} from "../data/member-invite-history";
import {
  fetchMemberInvites,
  INVITE_ROLES,
  inviteAccountMember,
  revokeMemberInvite,
  SAFE_MEMBER_INVITE_FAILURE,
  type InviteRole,
  type MemberInviteRow,
} from "../data/member-invites";
import { platformPeopleDisplayName } from "../data/platform-people-search";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime } from "../lib/display-time";

function displayNameRequired(role: InviteRole): boolean {
  return role === "guard";
}

function consoleRedirectOrigin(): string {
  if (typeof window === "undefined" || typeof window.location?.origin !== "string") {
    return "https://console.thesecops.app";
  }
  return window.location.origin;
}

function isInviteRole(value: string): value is InviteRole {
  return (INVITE_ROLES as readonly string[]).includes(value);
}

export type PlatformMemberInviteRequest =
  | { kind: "prefill-email"; email: string; requestId: number }
  | { kind: "inspect-invite"; inviteId: string; requestId: number }
  | { kind: "resend-invite"; inviteId: string; requestId: number }
  | { kind: "revoke-invite"; inviteId: string; requestId: number };

const EMPTY_INVITE_AUTH_HINTS: ReadonlyMap<string, InviteAuthHint> = new Map();

// Member invite panel (CONSOLE-MEMBER-INVITE-FORM): company admin reserves membership and
// sends a Supabase Auth invite email so the person appears in Auth.Users and can sign in.
// Pending-list Resend retries the Auth email for rows that never got one (or lost it).
// Slice 1 invite honesty: join readiness + stuck drawer + revoke-to-invite-again prefill.

export function MemberInvitePanel({
  client,
  accountId,
  timeZone = "America/New_York",
  canManage = false,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone?: string;
  /** member.manage - the invite form + revoke only exist when true (fail closed). */
  canManage?: boolean;
}) {
  return (
    <MemberInvitePanelSurface
      client={client}
      accountId={accountId}
      canManage={canManage}
      presentation={{ timeZone, redactContacts: false, showOwnerLabels: false }}
      onRowsSnapshot={undefined}
      platformRequest={null}
    />
  );
}

/** Platform Accounts presentation of the existing invite and invite-lifecycle surface. */
export function PlatformMemberInvitePanel({
  client,
  accountId,
  timeZone = "America/New_York",
  canManage = false,
  onRowsSnapshot,
  request = null,
  reloadSignal = 0,
}: {
  client: SupabaseClient;
  accountId: string;
  timeZone?: string;
  canManage?: boolean;
  onRowsSnapshot?: (
    rows: readonly MemberInviteRow[] | null,
    authHints: ReadonlyMap<string, InviteAuthHint>,
  ) => void;
  request?: PlatformMemberInviteRequest | null;
  /** Bump to force a pending-invite reload (bulk invite completion). */
  reloadSignal?: number;
}) {
  return (
    <MemberInvitePanelSurface
      client={client}
      accountId={accountId}
      canManage={canManage}
      presentation={{ timeZone, redactContacts: true, showOwnerLabels: true }}
      onRowsSnapshot={onRowsSnapshot}
      platformRequest={request}
      reloadSignal={reloadSignal}
    />
  );
}

function MemberInvitePanelSurface({
  client,
  accountId,
  canManage,
  presentation,
  onRowsSnapshot,
  platformRequest,
  reloadSignal = 0,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  presentation: {
    timeZone: string;
    redactContacts: boolean;
    showOwnerLabels: boolean;
  };
  onRowsSnapshot:
    | ((
        rows: readonly MemberInviteRow[] | null,
        authHints: ReadonlyMap<string, InviteAuthHint>,
      ) => void)
    | undefined;
  platformRequest: PlatformMemberInviteRequest | null;
  reloadSignal?: number;
}) {
  const { timeZone, redactContacts, showOwnerLabels } = presentation;
  const {
    status: inviteStatus,
    data: inviteData,
    error: inviteError,
    reload: reloadInvites,
  } = useReadModel<MemberInviteRow[]>(
    () => fetchMemberInvites(client, accountId),
    [client, accountId],
  );
  const {
    status: historyStatus,
    data: historyData,
    error: historyError,
    reload: reloadHistory,
  } = useReadModel<MemberInviteHistoryPage>(
    () => fetchMemberInviteHistory(client, accountId),
    [client, accountId],
  );
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [role, setRole] = useState<InviteRole>("guard");
  const [busy, setBusy] = useState(false);
  const [formFailure, setFormFailure] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<MemberInviteRow | null>(null);
  const [revokeFailure, setRevokeFailure] = useState<string | null>(null);
  const [resendBusyId, setResendBusyId] = useState<string | null>(null);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [inspecting, setInspecting] = useState<MemberInviteRow | null>(null);
  const [authHints, setAuthHints] = useState<ReadonlyMap<string, InviteAuthHint>>(
    () => new Map(),
  );
  const [revealedInviteIds, setRevealedInviteIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const emailInputRef = useRef<HTMLInputElement>(null);
  const handledRequestId = useRef<number | null>(null);

  useEffect(() => {
    onRowsSnapshot?.(
      inviteStatus === "ready" ? (inviteData ?? []) : null,
      inviteStatus === "ready" ? authHints : EMPTY_INVITE_AUTH_HINTS,
    );
  }, [authHints, inviteData, inviteStatus, onRowsSnapshot]);

  useEffect(() => {
    if (reloadSignal <= 0) return;
    reloadInvites();
  }, [reloadInvites, reloadSignal]);

  useEffect(() => {
    if (platformRequest === null || handledRequestId.current === platformRequest.requestId) return;

    if (platformRequest.kind === "prefill-email") {
      handledRequestId.current = platformRequest.requestId;
      if (!canManage) return;
      setEmail(platformRequest.email);
      setFormFailure(null);
      setFormSuccess(null);
      emailInputRef.current?.focus();
      return;
    }

    if (inviteStatus !== "ready") return;
    handledRequestId.current = platformRequest.requestId;
    const invite = (inviteData ?? []).find(
      (candidate) =>
        candidate.inviteId === platformRequest.inviteId && candidate.status === "pending",
    );
    if (invite === undefined) return;

    if (platformRequest.kind === "inspect-invite") {
      setInspecting(invite);
      return;
    }
    if (!canManage) return;
    if (platformRequest.kind === "resend-invite") {
      void resendAuthInvite(invite);
      return;
    }
    setRevokeFailure(null);
    setRevoking(invite);
  }, [canManage, inviteData, inviteStatus, platformRequest]);

  if (inviteStatus === "loading" && inviteData === null) {
    return <p className="surface__state" role="status">Loading invites...</p>;
  }
  if (inviteStatus === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {inviteError ?? "Could not load member invites."}
      </p>
    );
  }

  const invites = inviteData ?? [];
  const nameRequired = displayNameRequired(role);

  function rememberAuthHint(inviteId: string, hint: InviteAuthHint): void {
    setAuthHints((current) => {
      const next = new Map(current);
      if (hint === null) next.delete(inviteId);
      else next.set(inviteId, hint);
      return next;
    });
  }

  function prefillInviteAgain(
    invite: Pick<MemberInviteRow, "email" | "displayName" | "role"> & {
      contactPhone?: string | null;
    },
    note = "Previous invite revoked. Review the form and Send invite again.",
  ): void {
    setEmail(invite.email);
    setDisplayName(invite.displayName ?? "");
    setContactPhone(invite.contactPhone ?? "");
    setRole(isInviteRole(invite.role) ? invite.role : "guard");
    setFormFailure(null);
    setFormSuccess(note);
    setResendNote(null);
    emailInputRef.current?.focus();
  }

  async function submitInvite(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    const trimmedName = displayName.trim();
    if (nameRequired && trimmedName.length === 0) {
      setFormFailure("Display name is required for guard invites.");
      return;
    }
    setBusy(true);
    setFormFailure(null);
    setFormSuccess(null);
    try {
      const outcome = await inviteAccountMember(
        client,
        accountId,
        email.trim(),
        role,
        trimmedName.length > 0 ? trimmedName : null,
        contactPhone.trim().length > 0 ? contactPhone.trim() : null,
        null,
      );
      if (!outcome.ok) {
        setFormFailure(outcome.reason);
        return;
      }
      if (outcome.inviteId === null) {
        setFormFailure(SAFE_MEMBER_INVITE_FAILURE);
        return;
      }
      const authMail = await sendMemberAuthInviteEmail(
        client,
        accountId,
        outcome.inviteId,
        consoleRedirectOrigin(),
      );
      setEmail("");
      setDisplayName("");
      setContactPhone("");
      setRole("guard");
      reloadInvites();
      reloadHistory();
      if (authMail.ok) {
        rememberAuthHint(outcome.inviteId, authMail.kind);
        setFormSuccess(successNoteForAuthInvite(authMail));
      } else {
        setFormFailure(authMail.reason);
      }
    } catch {
      setFormFailure(SAFE_MEMBER_INVITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  async function confirmRevoke(): Promise<void> {
    if (busy || revoking === null) return;
    const target = revoking;
    setBusy(true);
    setRevokeFailure(null);
    try {
      const outcome = await revokeMemberInvite(client, accountId, target.inviteId);
      if (outcome.ok) {
        setRevoking(null);
        setInspecting(null);
        rememberAuthHint(target.inviteId, null);
        prefillInviteAgain(target);
        reloadInvites();
        reloadHistory();
      } else {
        setRevokeFailure(outcome.reason);
      }
    } catch {
      setRevokeFailure(SAFE_MEMBER_INVITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  async function resendAuthInvite(invite: MemberInviteRow): Promise<void> {
    if (resendBusyId !== null) return;
    setResendBusyId(invite.inviteId);
    setResendNote(null);
    setFormFailure(null);
    try {
      const authMail = await sendMemberAuthInviteEmail(
        client,
        accountId,
        invite.inviteId,
        consoleRedirectOrigin(),
      );
      if (authMail.ok) {
        rememberAuthHint(invite.inviteId, authMail.kind);
        setResendNote(successNoteForAuthInvite(authMail));
      } else {
        setResendNote(authMail.reason);
      }
    } finally {
      setResendBusyId(null);
    }
  }

  return (
    <div className="invite-panel">
      {canManage ? (
        <form className="invite-panel__form" aria-label="Invite a member" onSubmit={(e) => void submitInvite(e)}>
          <div className="invite-panel__grid">
            <label className="tpl__field invite-panel__field invite-panel__field--name">
              Display name
              <input
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder={nameRequired ? "Jordan Reyes" : "Optional"}
                required={nameRequired}
                autoComplete="name"
              />
            </label>
            <label className="tpl__field invite-panel__field invite-panel__field--email">
              Email
              <input
                ref={emailInputRef}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="person@company.com"
                required
              />
            </label>
            <label className="tpl__field invite-panel__field invite-panel__field--phone">
              Phone <span className="tpl__optional">(optional)</span>
              <input
                type="tel"
                value={contactPhone}
                onChange={(event) => setContactPhone(event.target.value)}
                placeholder="(502) 555-0100"
                autoComplete="tel"
              />
            </label>
            <label className="tpl__field invite-panel__field invite-panel__field--role">
              Role
              <select value={role} onChange={(event) => setRole(event.target.value as InviteRole)}>
                {INVITE_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {humanizeLabel(r)}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="minter__confirm-go invite-panel__send" disabled={busy}>
              Send invite
            </button>
          </div>
          <p className="page__note">
            Send invite reserves membership for this email and requests a sign-in invite. Resend
            sign-in email only retries Auth mail for an existing pending row. Pending is not proof
            the message reached an inbox.
          </p>
          {formFailure !== null ? (
            <p className="surface__state surface__state--error" role="alert">{formFailure}</p>
          ) : null}
          {formSuccess !== null ? (
            <p className="surface__state" role="status">{formSuccess}</p>
          ) : null}
        </form>
      ) : null}

      {resendNote !== null ? (
        <p className="surface__state" role="status">{resendNote}</p>
      ) : null}

      {invites.length === 0 ? (
        <p className="surface__state" role="status">
          No pending invites{canManage ? " - invite a supervisor or guard above" : ""}.
        </p>
      ) : (
        <ul className="invite-panel__rows">
          {invites.map((invite) => {
            const authHint = authHints.get(invite.inviteId) ?? null;
            const renderedName = redactContacts
              ? platformPeopleDisplayName(invite.displayName, invite.inviteId, "Invited member")
              : (invite.displayName ?? "Invited member");
            const readiness = joinReadinessForInvite({
              status: invite.status,
              authHint,
            });
            return (
              <li key={invite.inviteId} className="invite-panel__row">
                <span className="invite-panel__email">
                  {invite.displayName !== null || redactContacts ? (
                    <>
                      <strong>{renderedName}</strong>
                      <span className="invite-panel__email-sub">
                        {inviteContactValue(
                          invite.email,
                          "email",
                          redactContacts,
                          revealedInviteIds.has(invite.inviteId),
                        )}
                      </span>
                      {invite.contactPhone !== null ? (
                        <span className="invite-panel__email-sub">
                          {inviteContactValue(
                            invite.contactPhone,
                            "phone",
                            redactContacts,
                            revealedInviteIds.has(invite.inviteId),
                          )}
                        </span>
                      ) : null}
                      {redactContacts ? (
                        <button
                          type="button"
                          className="member-directory__reveal"
                          aria-pressed={revealedInviteIds.has(invite.inviteId)}
                          onClick={() => {
                            setRevealedInviteIds((current) => {
                              const next = new Set(current);
                              if (next.has(invite.inviteId)) next.delete(invite.inviteId);
                              else next.add(invite.inviteId);
                              return next;
                            });
                          }}
                        >
                          {revealedInviteIds.has(invite.inviteId) ? "Hide contact" : "Reveal contact"}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    invite.email
                  )}
                  <span
                    className={`invite-panel__readiness invite-panel__readiness--${readiness.tone}`}
                  >
                    {readiness.label}
                  </span>
                </span>
                <span className="schedule__chip schedule__chip--credential">
                  {humanizeLabel(invite.role)}
                  {showOwnerLabels && invite.role === "admin" ? " · owner on accept" : ""}
                </span>
                <span
                  className={`schedule__chip ${invite.status === "pending" ? "schedule__chip--partial" : "schedule__chip--draft"}`}
                >
                  {invite.status}
                </span>
                {invite.expiresAt !== null ? (
                  <span className="invite-panel__expiry">
                    expires {new Date(invite.expiresAt).toLocaleDateString()}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="builder__navbtn"
                  onClick={() => setInspecting(invite)}
                >
                  Why can&apos;t they get in?
                </button>
                {canManage && invite.status === "pending" ? (
                  <>
                    <button
                      type="button"
                      className="builder__navbtn"
                      disabled={resendBusyId !== null}
                      onClick={() => void resendAuthInvite(invite)}
                    >
                      {resendBusyId === invite.inviteId ? "Sending..." : "Resend sign-in email"}
                    </button>
                    <button
                      type="button"
                      className="builder__cancel"
                      onClick={() => {
                        setRevokeFailure(null);
                        setRevoking(invite);
                      }}
                    >
                      Revoke
                    </button>
                  </>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <RecentInviteHistory
        key={accountId}
        status={historyStatus}
        data={historyData}
        error={historyError}
        timeZone={timeZone}
        canManage={canManage}
        redactContacts={redactContacts}
        onInviteAgain={(invite) =>
          prefillInviteAgain(
            invite,
            "Invite details prefilled. Review the form and choose Send invite.",
          )
        }
      />

      {inspecting !== null ? (
        <div
          className="minter__confirm invite-panel__truth"
          role="dialog"
          aria-label="Why can't they get in?"
        >
          <h3 className="minter__confirm-title">Why can&apos;t they get in?</h3>
          <p className="minter__confirm-warning">
            {redactContacts
              ? platformPeopleDisplayName(
                  inspecting.displayName,
                  inspecting.inviteId,
                  "Invited member",
                )
              : (inspecting.displayName ?? "Invited member")} · {humanizeLabel(inspecting.role)} ·{" "}
            {inspecting.status}
          </p>
          <ul className="invite-panel__truth-list">
            {inviteTruthChecklist({
              status: inspecting.status,
              expiresAt: inspecting.expiresAt,
              authHint: authHints.get(inspecting.inviteId) ?? null,
            }).map((item) => (
              <li key={item.id} className={`invite-panel__truth-item invite-panel__truth-item--${item.tone}`}>
                <strong>{item.title}</strong>
                <span>{item.detail}</span>
              </li>
            ))}
          </ul>
          <div className="minter__confirm-actions">
            <button type="button" className="builder__navbtn" onClick={() => setInspecting(null)}>
              Close
            </button>
            {canManage && inspecting.status === "pending" ? (
              <>
                <button
                  type="button"
                  className="builder__navbtn"
                  disabled={resendBusyId !== null}
                  onClick={() => void resendAuthInvite(inspecting)}
                >
                  {resendBusyId === inspecting.inviteId ? "Sending..." : "Resend sign-in email"}
                </button>
                <button
                  type="button"
                  className="builder__cancel"
                  onClick={() => {
                    setRevokeFailure(null);
                    setRevoking(inspecting);
                  }}
                >
                  Revoke
                </button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {canManage && revoking !== null ? (
        <div
          className="minter__confirm"
          role="dialog"
          aria-label={`Revoke invite - ${
            redactContacts
              ? platformPeopleDisplayName(revoking.displayName, revoking.inviteId, "invited member")
              : revoking.email
          }`}
        >
          <h3 className="minter__confirm-title">
            Revoke invite - {redactContacts
              ? platformPeopleDisplayName(revoking.displayName, revoking.inviteId, "invited member")
              : revoking.email}
          </h3>
          <p className="minter__confirm-warning">
            The pending invite is cancelled. They can be re-invited later. Anyone who already
            accepted is unaffected. After confirm, the invite form prefills so you can Invite again.
          </p>
          {revokeFailure !== null ? (
            <p className="surface__state surface__state--error" role="alert">{revokeFailure}</p>
          ) : null}
          <div className="minter__confirm-actions">
            <button type="button" className="builder__navbtn" disabled={busy} onClick={() => setRevoking(null)}>
              Cancel
            </button>
            <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void confirmRevoke()}>
              Confirm revoke
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RecentInviteHistory({
  status,
  data,
  error,
  timeZone,
  canManage,
  redactContacts,
  onInviteAgain,
}: {
  status: "loading" | "ready" | "error";
  data: MemberInviteHistoryPage | null;
  error: string | null;
  timeZone: string;
  canManage: boolean;
  redactContacts: boolean;
  onInviteAgain: (invite: MemberInviteHistoryRow) => void;
}) {
  const [revealedInviteIds, setRevealedInviteIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  return (
    <section className="invite-history" aria-labelledby="recent-invites-title">
      <div className="invite-history__head">
        <h3 id="recent-invites-title">Recent invites</h3>
        <span>Selected workspace</span>
      </div>
      {status === "loading" && data === null ? (
        <p className="surface__state" role="status">Loading recent invite history...</p>
      ) : status === "error" ? (
        <p className="surface__state surface__state--error" role="alert">
          {error ?? "Could not load recent invite history."}
        </p>
      ) : (data?.rows.length ?? 0) === 0 ? (
        <p className="surface__state" role="status">No recent invite history.</p>
      ) : (
        <>
          <table className="invite-history__table" aria-label="Recent invite history">
            <thead>
              <tr>
                <th scope="col">Person/email</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">When</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {data!.rows.map((invite) => {
                const contactRevealed = revealedInviteIds.has(invite.inviteId);
                const revealRequired = redactContacts && !contactRevealed;
                const actionDisabled = !invite.canInviteAgain || revealRequired;
                const actionReason = !invite.canInviteAgain
                  ? invite.inviteAgainReason
                  : revealRequired
                    ? "Reveal contact before inviting again."
                    : null;
                const renderedName = platformPeopleDisplayName(
                  invite.displayName,
                  invite.inviteId,
                  "Invited member",
                );
                const when = invite.updatedAt ?? invite.createdAt;
                return (
                  <tr key={invite.inviteId} className="invite-history__row">
                    <td data-label="Person/email">
                      <span className="invite-history__person">
                        <strong>{renderedName}</strong>
                        <span>
                          {inviteContactValue(
                            invite.email,
                            "email",
                            redactContacts,
                            contactRevealed,
                          )}
                        </span>
                        {redactContacts ? (
                          <button
                            type="button"
                            className="member-directory__reveal"
                            aria-label={`${contactRevealed ? "Hide" : "Reveal"} contact for ${renderedName}`}
                            aria-pressed={contactRevealed}
                            onClick={() => {
                              setRevealedInviteIds((current) => {
                                const next = new Set(current);
                                if (next.has(invite.inviteId)) next.delete(invite.inviteId);
                                else next.add(invite.inviteId);
                                return next;
                              });
                            }}
                          >
                            {contactRevealed ? "Hide contact" : "Reveal contact"}
                          </button>
                        ) : null}
                      </span>
                    </td>
                    <td data-label="Role">{humanizeLabel(invite.role)}</td>
                    <td data-label="Status">
                      <span className="invite-history__status">{humanizeLabel(invite.status)}</span>
                    </td>
                    <td data-label="When">
                      <time dateTime={when}>{formatDisplayTime(when, timeZone)}</time>
                    </td>
                    <td data-label="Action" className="invite-history__action">
                      {canManage ? (
                        <>
                          <button
                            type="button"
                            className="builder__navbtn"
                            disabled={actionDisabled}
                            onClick={() => onInviteAgain(invite)}
                          >
                            Invite again
                          </button>
                          {actionReason !== null ? <span>{actionReason}</span> : null}
                        </>
                      ) : (
                        <span>Read only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data?.capped ? (
            <p className="invite-history__cap" role="status">
              Showing the {MEMBER_INVITE_HISTORY_LIMIT} most recent invite history rows for this
              workspace. Older invite history is outside this view.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function inviteContactValue(
  value: string,
  kind: "email" | "phone",
  redact: boolean,
  revealed: boolean,
): string {
  if (!redact || revealed) return value;
  if (kind === "phone") {
    const tail = value.replace(/\D/gu, "").slice(-4);
    return tail.length > 0 ? `••• ${tail}` : "•••";
  }
  const separator = value.indexOf("@");
  if (separator <= 0) return "•••";
  return `${value.slice(0, 1)}•••${value.slice(separator)}`;
}
